// Standalone HTML builder for the printable Attention Report. No React, no
// browser globals beyond `Date` — pure string output. Opens in a new window
// from Dashboard.js when the user clicks "Generate Report".

import { parseIsoOrDash, fmtDateShort } from "./utils";

const STAGE_COLOR = {
  "PO Received": "#3b82f6", "Procurement": "#f97316",
  "Manufacturing": "#a855f7", "Quality Control": "#14b8a6",
  "Dispatch": "#f59e0b", "Completed": "#22c55e",
};

const F_BODY = "'Trebuchet MS',Tahoma,Geneva,Helvetica,Arial,sans-serif";
const F_MONO = "'Courier New',Courier,monospace";
const F_HEAD = "Arial,Helvetica,sans-serif";

const _h = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;")
  .replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function buildAttentionReportHTML({ items, catalogue, companyName, reportedBy }) {
  const now = new Date();
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const in7 = new Date(today); in7.setDate(today.getDate() + 7);
  // Bucket items
  const active = items.filter(i => i.stage !== "Completed");
  const blocked = active.filter(i => i.is_blocked);
  const rework = active.filter(i => i.is_rework);
  const overdue = active.map(i => {
    const d = parseIsoOrDash(i.dispatch_due_date || i.due_date);
    return d && d < today ? { ...i, _overdueDays: Math.floor((today - d) / 86400000) } : null;
  }).filter(Boolean);
  const dueSoon = active.map(i => {
    const d = parseIsoOrDash(i.dispatch_due_date || i.due_date);
    return d && d >= today && d <= in7 ? { ...i, _daysLeft: Math.ceil((d - today) / 86400000) } : null;
  }).filter(Boolean);
  const stuck = active.map(i => {
    const sa = (i.stage_ages || {})[i.stage];
    if (!sa) return null;
    let totalS = Number(sa.total_seconds || 0);
    if (sa.entered_at) {
      try { totalS += (Date.now() - new Date(sa.entered_at).getTime()) / 1000; } catch (e) { /* noop */ }
    }
    return totalS >= 7 * 86400 ? { ...i, _stageDays: Math.floor(totalS / 86400) } : null;
  }).filter(Boolean);

  // Flatten reason-code catalogue for lookup
  const flat = {};
  if (catalogue) {
    Object.entries(catalogue).forEach(([stage, cfg]) => {
      if (cfg.uses_subteam) {
        Object.entries(cfg.subteams || {}).forEach(([sub, codes]) => {
          codes.forEach(c => { flat[c.code] = { ...c, stage, subteam: sub }; });
        });
      } else {
        (cfg.codes || []).forEach(c => { flat[c.code] = { ...c, stage, subteam: "" }; });
      }
    });
  }

  // Reason-code ranking (this month)
  const monthStart = new Date(today); monthStart.setDate(1);
  const reasonCounts = {};
  items.forEach(i => {
    if (!i.is_blocked) return;
    const u = i.updated_at ? new Date(i.updated_at) : null;
    if (!u || u < monthStart) return;
    const code = (i.block_reason_code || "UNCODED").trim() || "UNCODED";
    reasonCounts[code] = (reasonCounts[code] || 0) + 1;
  });
  const topReasons = Object.entries(reasonCounts).map(([code, count]) => ({
    code, count,
    description: flat[code]?.description || (code === "UNCODED" ? "Uncoded / free-text reason" : code),
  })).sort((a, b) => b.count - a.count).slice(0, 10);

  const sumCard = (n, label, color) =>
    `<td style="padding:14px 6px;text-align:center;vertical-align:middle">
      <div style="font-size:28px;font-weight:700;color:${color};font-family:${F_BODY};line-height:1">${n}</div>
      <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#6b7280;margin-top:6px;font-family:${F_HEAD}">${label}</div>
    </td>`;

  const section = (title, subtitle, rows, emptyMsg, accent) => {
    if (rows.length === 0) {
      return `<div style="margin:18px 0;background:#ffffff;border:1px solid #e2e8f0;border-radius:8px;padding:16px 20px">
        <div style="border-left:3px solid ${accent};padding-left:12px">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:${accent};font-family:${F_HEAD}">${title}</div>
          <div style="font-size:11px;color:#6b7280;margin-top:3px;font-family:${F_BODY}">${subtitle}</div>
        </div>
        <p style="margin:10px 0 0 15px;font-size:12px;color:#64748b;font-style:italic;font-family:${F_BODY}">${emptyMsg}</p>
      </div>`;
    }
    return `<div style="margin:18px 0;background:#ffffff;border:1px solid #e2e8f0;border-radius:8px;padding:16px 20px;break-inside:avoid;page-break-inside:avoid">
      <div style="border-left:3px solid ${accent};padding-left:12px;margin-bottom:12px">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:${accent};font-family:${F_HEAD}">${title}</div>
        <div style="font-size:11px;color:#6b7280;margin-top:3px;font-family:${F_BODY}">${subtitle} &nbsp;·&nbsp; ${rows.length} item${rows.length !== 1 ? "s" : ""}</div>
      </div>
      <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-family:${F_BODY};font-size:11px">
        <thead><tr style="background:#f8fafc;border-bottom:2px solid #e2e8f0">
          ${rows[0]._cols.map(c => `<th style="padding:7px 9px;text-align:${c.align || "left"};font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#64748b;font-family:${F_HEAD}">${c.h}</th>`).join("")}
        </tr></thead>
        <tbody>${rows.map((r, i) =>
          `<tr style="${i % 2 ? "background:#fafbfc" : ""};border-bottom:1px solid #f1f5f9">${r._cols.map(c => `<td style="padding:8px 9px;vertical-align:top;${c.align === "right" ? "text-align:right" : ""}">${c.v}</td>`).join("")}</tr>`
        ).join("")}</tbody>
      </table>
    </div>`;
  };

  const item_col = (i) => {
    const cc = STAGE_COLOR[i.stage] || "#94a3b8";
    return `<span style="font-family:${F_MONO};font-size:11px;color:#0f172a;font-weight:700">
        <span style="font-size:9px;color:#64748b;font-weight:700;letter-spacing:0.08em;margin-right:3px">PO</span>${_h(i.po_number)}${i.line_number ? `<span style="opacity:0.4;margin:0 4px">·</span><span style="font-size:9px;color:#64748b;font-weight:700;letter-spacing:0.08em;margin-right:3px">LINE</span>${_h(i.line_number)}` : ""}
      </span>
      <div style="font-size:12px;color:#0f172a;margin-top:1px">${_h(i.item_description || "")}</div>
      <div style="font-size:10px;color:#64748b;margin-top:1px">
        <span style="color:${cc};font-weight:700">${_h(i.stage)}</span>
        &nbsp;·&nbsp; ${_h(i.client)}
        ${i.part_number ? `&nbsp;·&nbsp; Part <span style="font-family:${F_MONO}">${_h(i.part_number)}</span>` : ""}
      </div>`;
  };

  // Age + actor cell — elegant stacked layout for Blocked / Rework sections.
  const buildAgeCell = (isoTs, actor, actorRole, accent) => {
    let ageStr = "", dateStr = "";
    if (isoTs) {
      try {
        const t = new Date(isoTs);
        const secs = Math.max(0, (Date.now() - t.getTime()) / 1000);
        const d = Math.floor(secs / 86400);
        const h = Math.floor((secs % 86400) / 3600);
        const m = Math.floor((secs % 3600) / 60);
        if (d >= 1) ageStr = `${d}d ${h}h`;
        else if (h >= 1) ageStr = `${h}h ${m}m`;
        else ageStr = `${m}m`;
        const mons = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        dateStr = `${t.getDate()} ${mons[t.getMonth()]}'${String(t.getFullYear()).slice(2)}`;
      } catch (e) { /* noop */ }
    }
    if (!ageStr && !actor) return `<span style="color:#94a3b8;font-size:10px;font-style:italic">—</span>`;
    const age_html = ageStr
      ? `<div style="font-size:12px;font-weight:700;color:${accent};font-family:${F_BODY};line-height:1.1">${_h(ageStr)}</div>`
      : "";
    const date_html = dateStr
      ? `<div style="font-size:10px;color:#94a3b8;font-family:${F_HEAD};margin-top:1px">since ${_h(dateStr)}</div>`
      : "";
    const role_tag = actorRole
      ? `<span style="color:#94a3b8;margin-left:3px;font-size:10px;font-family:${F_HEAD}">(${_h(actorRole)})</span>`
      : "";
    const actor_html = actor
      ? `<div style="font-size:10px;color:#64748b;margin-top:2px;font-family:${F_BODY}">by <span style="color:#334155;font-weight:600">${_h(actor)}</span>${role_tag}</div>`
      : "";
    return age_html + date_html + actor_html;
  };

  const blockedRows = blocked.map(i => ({
    _cols: [
      { h: "Item", v: item_col(i) },
      { h: "Reason code", v: i.block_reason_code
          ? `<span style="display:inline-block;font-family:${F_MONO};font-weight:700;padding:2px 6px;border-radius:3px;background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;font-size:10px;white-space:nowrap">${_h(i.block_reason_code)}</span>`
          : `<span style="font-size:10px;color:#94a3b8;font-style:italic">Uncoded</span>` },
      { h: "Details", v: `<div style="font-size:11px;color:#334155;max-width:300px">${_h(i.block_reason || "—")}</div>` },
      { h: "Blocked for", v: buildAgeCell(i.blocked_at, i.blocked_by, i.blocked_by_role, "#b91c1c") },
      { h: "Qty", v: `<span style="font-family:${F_MONO};color:#334155">${_h(i.quantity)} ${_h(i.unit)}</span>`, align: "right" },
    ],
  }));

  const reworkRows = rework.map(i => ({
    _cols: [
      { h: "Item", v: item_col(i) },
      { h: "Return reason", v: `<div style="font-size:11px;color:#92400e;max-width:300px">${_h(i.return_reason || "—")}</div>` },
      { h: "In rework", v: buildAgeCell(i.rework_at, i.rework_by, i.rework_by_role, "#b91c1c") },
      { h: "Qty", v: `<span style="font-family:${F_MONO};color:#334155">${_h(i.quantity)} ${_h(i.unit)}</span>`, align: "right" },
    ],
  }));

  const overdueRows = overdue.sort((a, b) => b._overdueDays - a._overdueDays).slice(0, 100).map(i => ({
    _cols: [
      { h: "Item", v: item_col(i) },
      { h: "Due", v: `<span style="font-family:${F_MONO};color:#64748b">${fmtDateShort(i.dispatch_due_date || i.due_date)}</span>` },
      { h: "Overdue", v: `<span style="font-weight:700;color:#b91c1c">${i._overdueDays}d</span>`, align: "right" },
      { h: "Qty", v: `<span style="font-family:${F_MONO};color:#334155">${_h(i.quantity)} ${_h(i.unit)}</span>`, align: "right" },
    ],
  }));

  const dueSoonRows = dueSoon.sort((a, b) => a._daysLeft - b._daysLeft).map(i => ({
    _cols: [
      { h: "Item", v: item_col(i) },
      { h: "Due", v: `<span style="font-family:${F_MONO};color:#64748b">${fmtDateShort(i.dispatch_due_date || i.due_date)}</span>` },
      { h: "In", v: `<span style="font-weight:700;color:#b45309">${i._daysLeft}d</span>`, align: "right" },
      { h: "Qty", v: `<span style="font-family:${F_MONO};color:#334155">${_h(i.quantity)} ${_h(i.unit)}</span>`, align: "right" },
    ],
  }));

  const stuckRows = stuck.sort((a, b) => b._stageDays - a._stageDays).slice(0, 100).map(i => ({
    _cols: [
      { h: "Item", v: item_col(i) },
      { h: "In stage", v: `<span style="font-weight:700;color:#7e22ce">${i._stageDays}d</span>`, align: "right" },
      { h: "Qty", v: `<span style="font-family:${F_MONO};color:#334155">${_h(i.quantity)} ${_h(i.unit)}</span>`, align: "right" },
    ],
  }));

  const reasonBarMax = topReasons[0]?.count || 1;
  const reasonBars = topReasons.map(r => `
    <tr>
      <td style="padding:6px 10px 6px 0;white-space:nowrap">
        <span style="display:inline-block;font-family:${F_MONO};font-size:11px;font-weight:700;padding:2px 6px;border-radius:3px;background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;white-space:nowrap">${_h(r.code)}</span>
      </td>
      <td style="padding:6px 10px;width:100%">
        <div style="background:#fef2f2;border-radius:3px;height:20px;overflow:hidden;position:relative">
          <div style="width:${Math.max(8, (r.count / reasonBarMax) * 100)}%;background:#ef4444;height:100%;border-radius:3px;display:flex;align-items:center;padding:0 8px">
            <span style="font-size:10px;color:#ffffff;font-weight:600;font-family:${F_BODY};white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${_h(r.description)}</span>
          </div>
        </div>
      </td>
      <td style="padding:6px 0 6px 10px;text-align:right;white-space:nowrap;font-family:${F_BODY};font-size:12px;font-weight:700;color:#b91c1c">${r.count}</td>
    </tr>
  `).join("");

  const dateStr = now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>RockLink Attention Report — ${_h(dateStr)}</title>
<style>
  @media print {
    body { -webkit-print-color-adjust:exact;print-color-adjust:exact; margin:0;}
    .no-print { display:none !important; }
    .section { page-break-inside:avoid; break-inside:avoid; }
  }
  body { margin:0; background:#eef2f7; }
</style>
</head>
<body style="font-family:${F_BODY};color:#0f172a">
<div style="max-width:960px;margin:0 auto;padding:24px 16px">

  <div class="no-print" style="margin-bottom:16px;text-align:right">
    <button onclick="window.print()" style="padding:10px 18px;background:#ea580c;color:#ffffff;border:0;border-radius:6px;font-weight:700;font-family:${F_HEAD};cursor:pointer;font-size:13px">Print / Save as PDF</button>
  </div>

  <div style="background:#ffffff;border-radius:10px 10px 0 0;border:1px solid #e2e8f0;border-bottom:none;overflow:hidden">
    <div style="height:4px;background:linear-gradient(to right,#ef4444,#f97316)"></div>
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td style="padding:20px 28px 16px;vertical-align:middle">
        <div style="font-size:26px;font-weight:800;letter-spacing:-0.5px;line-height:1;font-family:${F_HEAD}">
          <span style="color:#ef4444">Rock</span><span style="color:#f97316">Link</span>
        </div>
        <div style="font-size:9px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:#6b7280;margin-top:5px;font-family:${F_HEAD}">Attention Report</div>
      </td>
      <td style="padding:20px 28px 16px;text-align:right;vertical-align:middle">
        <div style="font-size:13px;font-weight:700;color:#0f172a;font-family:${F_HEAD};line-height:1.4">${_h(dateStr)}</div>
        <div style="font-size:11px;color:#64748b;margin-top:4px;font-family:${F_BODY}">Generated by ${_h(reportedBy)}</div>
        <div style="font-size:10px;color:#94a3b8;margin-top:2px;font-family:${F_HEAD}">${_h(companyName)}</div>
      </td>
    </tr></table>
    <div style="background:#f1f5f9;border-top:1px solid #e2e8f0;padding:10px 24px;text-align:center">
      <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:5px;color:#334155;font-family:${F_HEAD}">Items needing action</span>
    </div>
  </div>

  <div style="background:#ffffff;border:1px solid #e2e8f0;border-top:none">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      ${sumCard(blocked.length,  "Blocked",   "#ef4444")}
      <td style="border-right:1px solid #e5e7eb;width:0"></td>
      ${sumCard(overdue.length,  "Overdue",   "#f59e0b")}
      <td style="border-right:1px solid #e5e7eb;width:0"></td>
      ${sumCard(dueSoon.length,  "Due ≤ 7d",  "#b45309")}
      <td style="border-right:1px solid #e5e7eb;width:0"></td>
      ${sumCard(stuck.length,    "Stuck > 7d","#a855f7")}
      <td style="border-right:1px solid #e5e7eb;width:0"></td>
      ${sumCard(rework.length,   "Rework",    "#dc2626")}
    </tr></table>
  </div>

  ${topReasons.length ? `
  <div class="section" style="margin:18px 0;background:#ffffff;border:1px solid #e2e8f0;border-radius:8px;padding:16px 20px;break-inside:avoid;page-break-inside:avoid">
    <div style="border-left:3px solid #ef4444;padding-left:12px;margin-bottom:12px">
      <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#ef4444;font-family:${F_HEAD}">Top Blocking Reasons</div>
      <div style="font-size:11px;color:#6b7280;margin-top:3px;font-family:${F_BODY}">Ranked by frequency this month</div>
    </div>
    <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse">${reasonBars}</table>
  </div>` : ""}

  <div class="section">${section("Blocked items", "Workflow halted — requires action now", blockedRows, "Nothing blocked. Pipeline is clean.", "#ef4444")}</div>
  <div class="section">${section("Overdue items", "Dispatch / due date has passed", overdueRows, "No overdue items.", "#f59e0b")}</div>
  <div class="section">${section("Due within 7 days", "Imminent deliveries — plan ahead", dueSoonRows, "Nothing due this week.", "#b45309")}</div>
  <div class="section">${section("Stuck > 7 days in stage", "No movement in over a week", stuckRows, "No stuck items.", "#a855f7")}</div>
  <div class="section">${section("Rework items", "Returned for correction", reworkRows, "No rework in progress.", "#dc2626")}</div>

  <div style="background:#f1f5f9;border:1px solid #e2e8f0;border-top:none;border-radius:0 0 10px 10px;padding:12px 24px;margin-top:18px">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td style="font-size:11px;color:#64748b;font-family:${F_BODY}">
        <span style="font-weight:700;font-family:${F_HEAD}"><span style="color:#ef4444">Rock</span><span style="color:#f97316">Link</span></span>
        &nbsp;·&nbsp; ${_h(companyName)}
      </td>
      <td style="text-align:right;font-size:11px;color:#64748b;font-family:${F_BODY}">
        ${now.toLocaleString("en-US")} &nbsp;·&nbsp; ${items.length} items in pipeline
      </td>
    </tr></table>
  </div>
</div>
</body></html>`;
}
